/**
 * StudentFields - Form fields for student information in sign-up
 * 
 * Includes first name, last name, birthday picker, and gender selector.
 * Integrates with React Hook Form field arrays.
 * 
 * @example
 * ```tsx
 * {fields.map((field, index) => (
 *   <StudentFields
 *     key={field.id}
 *     index={index}
 *     field={field}
 *     remove={remove}
 *     errors={errors}
 *     register={register}
 *     control={control}
 *     fieldsLength={fields.length}
 *   />
 * ))}
 * ```
 */

"use client";

import { useState } from "react";
import {
  Box,
  Flex,
  SimpleGrid,
} from "@chakra-ui/react";
import {
  type Control,
  Controller,
  type FieldArrayWithId,
  type FieldErrors,
  type UseFieldArrayRemove,
  type UseFormRegister,
} from "react-hook-form";
import { FiTrash2 } from "react-icons/fi";
import { ConfirmationModal } from "@/components";
import { IconButton, Text, TouchAwareTooltip } from "@/components/ui";
import { useInteractiveTokens } from "@/lib/theme/tokens";
import { BirthdayPicker } from "@/components/forms";
import { LabeledInput } from "@/components/shared/formElements/LabeledInput";
import { CustomSelect } from "@/components/shared/formElements/CustomSelect";
import { STUDENT_GENDER_SELECT_OPTIONS } from "@/lib/utils/gender/student-gender";
import { useThemeTokens } from "@/lib/theme/tokens";
import type { SignUpFormSchema } from "@/schemas";

type StudentFieldsProps = {
  index: number;
  fieldsLength: number;
  remove: UseFieldArrayRemove;
  control: Control<SignUpFormSchema>;
  errors: FieldErrors<SignUpFormSchema>;
  register: UseFormRegister<SignUpFormSchema>;
  field: FieldArrayWithId<SignUpFormSchema, "students", "id">;
};

type StudentErrors = NonNullable<
  FieldErrors<SignUpFormSchema>["students"]
>[number];

export const StudentFields = ({
  index,
  field,
  remove,
  errors,
  control,
  register,
  fieldsLength,
}: StudentFieldsProps) => {
  const theme = useThemeTokens();
  const interactive = useInteractiveTokens();
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const studentErrors: StudentErrors | undefined = errors.students?.[index];

  const handleConfirmRemove = () => {
    remove(index);
    setIsRemoveModalOpen(false);
  };

  return (
    <Box key={field.id} mb="40px" pt="20px">
      <Flex mb="20px" height="40px" align="center" justify="space-between">
        <Text variant="primary" fontSize="lg" fontWeight="600">
          Student #{index + 1}
        </Text>

        {fieldsLength > 1 && (
          <TouchAwareTooltip label="Remove student" placement="top" openDelay={300}>
            <IconButton
              aria-label="Remove student"
              icon={<FiTrash2 size={18} />}
              size="sm"
              variant="ghost"
              color={theme.text.tertiary}
              onClick={() => setIsRemoveModalOpen(true)}
              sx={{
                "@media (hover: hover)": {
                  "&:hover": {
                    color: "red.400",
                    bg: interactive.neutral.bg,
                  },
                },
              }}
            />
          </TouchAwareTooltip>
        )}
      </Flex>

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing="20px">
        <LabeledInput
          name={`students.${index}.firstName` as any}
          label="First Name"
          errors={errors}
          register={register}
          placeholder="First Name"
          required
          />

        <LabeledInput
          name={`students.${index}.lastName` as any}
          label="Last Name"
          errors={errors}
          register={register}
          placeholder="Last Name"
          required
          />

        <Box>
          <Controller
            control={control}
            name={`students.${index}.birthday`}
            render={({ field: birthdayField }) => (
              <BirthdayPicker
                value={birthdayField.value || { day: "", month: "", year: "" }}
                onChange={(val) => {
                  birthdayField.onChange(val);
                  birthdayField.onBlur();
                }}
                error={studentErrors?.birthday as any}
                required
                help="*Important: Birthdate determines class eligibility."
              />
            )}
          />
        </Box>

        <Box>
          <Controller
            control={control}
            name={`students.${index}.gender`}
            render={({ field: genderField }) => (
              <CustomSelect
                label="Gender"
                placeholder="--"
                value={genderField.value ?? ""}
                options={[...STUDENT_GENDER_SELECT_OPTIONS]}
                onChange={(val) => {
                  genderField.onChange(val);
                  genderField.onBlur();
                }}
                formControlProps={{
                  isInvalid: !!studentErrors?.gender,
                }}
                data-testid="signup-student-gender"
                required
              />
            )}
          />

          {studentErrors?.gender && (
            <Text color={theme.accent.primary} fontSize="sm" mt="6px">
              {studentErrors.gender.message}
            </Text>
          )}
        </Box>
      </SimpleGrid>

      <ConfirmationModal
        data-testid="sign-up-remove-student-dialog"
        isOpen={isRemoveModalOpen}
        onClose={() => setIsRemoveModalOpen(false)}
        onConfirm={handleConfirmRemove}
        title="Remove student"
        message={`Are you sure you want to remove Student #${index + 1}?`}
        confirmText="Remove"
        confirmColorScheme="red"
        size="sm"
      />
    </Box>
  );
};

